"use client";

import * as React from "react";
import { Download, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { StatusBadge } from "./status-badge";
import { CopyButton } from "./copy-button";
import { TimestampCell } from "./timestamp-cell";
import { JsonViewer } from "./json-viewer";
import { ErrorState } from "@/components/shared/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { apiFetch, ApiClientError, isAbortError } from "@/lib/api/api-client";
import type { LogByIdApiResponse } from "@/types/api";
import type { SerializedLogRecord } from "@/types/logs";

interface LogDetailsDrawerProps {
  id: string | null;
  onOpenChange: (open: boolean) => void;
}

interface FieldProps {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}

function Field({ label, value, mono }: FieldProps) {
  const display = value && value.length > 0 ? value : "—";
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        {value ? <CopyButton value={value} label={label} /> : null}
      </div>
      <p className={mono ? "break-all font-mono text-xs" : "break-words text-sm"}>{display}</p>
    </div>
  );
}

function downloadRecordJson(record: SerializedLogRecord) {
  const safeId = record.id.replace(/[^a-zA-Z0-9-_]/g, "-");
  const filename = `log-record-${safeId}.json`;
  const blob = new Blob([JSON.stringify(record, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function LogDetailsDrawer({ id, onOpenChange }: LogDetailsDrawerProps) {
  const [record, setRecord] = React.useState<LogByIdApiResponse["record"] | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isMarkingFixed, setIsMarkingFixed] = React.useState(false);
  const [error, setError] = React.useState<ApiClientError | null>(null);

  React.useEffect(() => {
    if (!id) {
      setRecord(null);
      setError(null);
      return;
    }

    const controller = new AbortController();
    setIsLoading(true);
    setError(null);

    apiFetch<LogByIdApiResponse>(`/api/logs/${encodeURIComponent(id)}`, { signal: controller.signal })
      .then((response) => {
        setRecord(response.record);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (isAbortError(err)) return;
        setError(err instanceof ApiClientError ? err : new ApiClientError(500, "INTERNAL_ERROR", "Request failed"));
        setIsLoading(false);
      });

    return () => controller.abort();
  }, [id]);

  const handleMarkAsFixed = async () => {
    if (!record || record.isFixed || isMarkingFixed) return;

    setIsMarkingFixed(true);
    try {
      const response = await apiFetch<LogByIdApiResponse>(`/api/logs/${encodeURIComponent(record.id)}`, {
        method: "PATCH",
      });
      setRecord(response.record);
      toast.success("Log marked as fixed", { position: "top-right" });
    } catch {
      toast.error("Could not mark log as fixed. Please try again.", { position: "top-right" });
    } finally {
      setIsMarkingFixed(false);
    }
  };

  const handleDownload = () => {
    if (!record) return;
    try {
      downloadRecordJson(record);
      toast.success("Log record downloaded");
    } catch {
      toast.error("Download failed. Please try again.");
    }
  };

  return (
    <Sheet open={Boolean(id)} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full gap-0 overflow-y-auto sm:max-w-xl">
        <SheetHeader className="border-b pr-20">
          <SheetTitle>Log record details</SheetTitle>
          <SheetDescription>Full request and response detail for a single log entry.</SheetDescription>
        </SheetHeader>

        {record && !isLoading ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute top-4 right-12 size-6 opacity-70 hover:opacity-100"
                aria-label="Download log record as JSON"
                onClick={handleDownload}
              >
                <Download className="size-4" aria-hidden="true" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Download JSON</TooltipContent>
          </Tooltip>
        ) : null}

        <div className="space-y-5 p-4">
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-5 w-full" />
              ))}
            </div>
          ) : null}

          {error ? <ErrorState title="Could not load this record" description={error.message} /> : null}

          {record && !isLoading ? (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge classification={record.errorClassification} />
                <span className="text-xs text-muted-foreground">{record.method}</span>
                {record.statusCode !== undefined ? (
                  <span className="text-xs text-muted-foreground">· HTTP {record.statusCode}</span>
                ) : null}
                {record.isFixed ? (
                  <span className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                    <CheckCircle2 className="size-3" aria-hidden="true" />
                    Fixed
                  </span>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleMarkAsFixed}
                    disabled={isMarkingFixed}
                  >
                    {isMarkingFixed ? "Marking…" : "Mark as fixed"}
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="User ID" value={record.userId} mono />
                <Field label="Request ID" value={record.id} mono />
                <Field label="Original route" value={record.route} mono />
                <Field label="Normalized route" value={record.normalizedRoute} mono />
                <Field label="Error type" value={record.errorType} />
                <Field label="Error message" value={record.errorMessage} />
              </div>

              <Separator />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground">Created</span>
                  <div className="text-sm">
                    <TimestampCell value={record.createdAt} />
                  </div>
                </div>
                <div className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground">Updated</span>
                  <div className="text-sm">
                    <TimestampCell value={record.updatedAt} />
                  </div>
                </div>
                <div className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground">Response stored</span>
                  <p className="text-sm">{record.responseStored ? "Yes" : "No"}</p>
                </div>
                <div className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground">Derived error state</span>
                  <p className="text-sm">
                    {record.isError === null ? "Unknown" : record.isError ? "Error" : "No error"}
                  </p>
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium">Parsed input</h3>
                  {record.inputMalformed ? <span className="text-xs text-warning">Malformed JSON</span> : null}
                </div>
                <JsonViewer data={record.inputParsed} emptyMessage="No input payload" />
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground">Raw input</summary>
                  <div className="mt-1 flex items-start gap-2 rounded-md border bg-muted/30 p-2">
                    <pre className="flex-1 overflow-x-auto whitespace-pre-wrap break-all">{record.inputRaw || "—"}</pre>
                    {record.inputRaw ? <CopyButton value={record.inputRaw} label="Raw input" /> : null}
                  </div>
                </details>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium">Parsed response</h3>
                  {record.responseMalformed ? <span className="text-xs text-warning">Malformed JSON</span> : null}
                </div>
                <JsonViewer data={record.responseParsed} emptyMessage="No response payload" />
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground">Raw response</summary>
                  <div className="mt-1 flex items-start gap-2 rounded-md border bg-muted/30 p-2">
                    <pre className="flex-1 overflow-x-auto whitespace-pre-wrap break-all">{record.responseRaw || "—"}</pre>
                    {record.responseRaw ? <CopyButton value={record.responseRaw} label="Raw response" /> : null}
                  </div>
                </details>
              </div>
            </>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
